import React from 'react';
import { Chip, CircularProgress } from 'material-ui';
import { lightBlue300 } from 'material-ui/styles/colors';
import ErrorIcon from 'material-ui/svg-icons/alert/error';
import _ from 'lodash';
import { connect } from 'react-redux';
import { compose, lifecycle, pure } from 'recompose';
import PropTypes from 'prop-types';
import humanize from 'humanize-string';
import { STORE_KEYS } from '../../constants';
import { getEntities, getQueries } from 'dash-front/selectors';
import { fetchTournamentById, fetchTeamById, fetchEventById } from 'dash-front/actions';
import { yCenter } from './helpers';

const space = <span>&nbsp;</span>;
const wrapperStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
};

const chipStyle = {
    margin: 4,
};

const infoChipCommon = {
    style: chipStyle,
    backgroundColor: lightBlue300,
};

const isAny = value => !value || value === ' ' || value === '*';

/**
 * Chip proxy with styles
 */
const InfoChip = props => {
    const { style: propStyles, ...restProps } = props;
    const { style: commonStyles } = infoChipCommon;
    const style = { ...commonStyles, ...propStyles };
    return React.createElement(Chip, { ...infoChipCommon, ...{ style }, ...restProps });
};
const Label = ({ value, requestFinished, requestPending }) => {
    if (!requestFinished && requestPending) {
        return <CircularProgress size={20} />;
    }
    if (requestFinished && !value) {
        return <ErrorIcon />;
    }
    return <span>{value}</span>;
};

/**
 * HOC lifecycle with fetch updates
 * @param {*} propKey
 * @param {*} fetch
 */
const getLifeCycleUpdates = (propKey, fetch) =>
    lifecycle({
        componentDidMount() {
            const { [propKey]: prop } = this.props;
            if (!isAny(prop)) {
                const { dispatch } = this.props;
                dispatch(fetch(prop));
            }
        },
        componentWillReceiveProps({ [propKey]: prop }) {
            if (!isAny(prop)) {
                const { dispatch } = this.props;
                dispatch(fetch(prop));
            }
        },
    });
/**
 * @param {String} storeKey
 * @param {String} propKey
 * @returns {function}
 */
const getMapStateToProps = (storeKey, propKey) => (state, { [propKey]: prop }) => {
    const value = _.get(getEntities(state)[storeKey], '[' + prop + '].name');
    const requestFinished = _.get(getQueries(state)[storeKey + '/' + prop], 'isFinished');
    const requestPending = _.get(getQueries(state)[storeKey + '/' + prop], 'isPending');
    return { value, requestFinished, requestPending };
};

/**
 * Components for diplaying additional
 * data from server with fetch logic
 */

/**
 * Tournaments Component
 */
const TT_KEY = STORE_KEYS.TOURNAMENTS + STORE_KEYS.BY_ID;
const tournamentLabelEnhancer = compose(
    connect(getMapStateToProps(TT_KEY, 'tournamentId')),
    getLifeCycleUpdates('tournamentId', fetchTournamentById),
    pure,
);
const TtLabelEnhanced = tournamentLabelEnhancer(Label);
TtLabelEnhanced.propTypes = {
    // eslint-disable-next-line
    tournamentId: PropTypes.any,
};

/**
 * Teams Component
 */
const teamsLabelEnhancer = compose(
    connect(getMapStateToProps(STORE_KEYS.TEAMS, 'teamId')),
    getLifeCycleUpdates('teamId', fetchTeamById),
    pure,
);
const TeamsLabelEnhanced = teamsLabelEnhancer(Label);
TeamsLabelEnhanced.propTypes = {
    // eslint-disable-next-line
    teamId: PropTypes.any,
};

/**
 * Events Component
 */
const EVENTS_KEY = STORE_KEYS.EVENTS;
const mapStateToEvents = (state, { eventId }) => {
    const value1 = _.get(getEntities(state)[EVENTS_KEY], '[' + eventId + '].homeName');
    const value2 = _.get(getEntities(state)[EVENTS_KEY], '[' + eventId + '].awayName');
    const value = value1 && value2 ? value1 + ' - ' + value2 : undefined;
    const requestFinished = _.get(getQueries(state)[EVENTS_KEY + '/' + eventId], 'isFinished');
    const requestPending = _.get(getQueries(state)[EVENTS_KEY + '/' + eventId], 'isPending');
    return { value, requestFinished, requestPending };
};
const eventLabelEnhancer = compose(connect(mapStateToEvents), getLifeCycleUpdates('eventId', fetchEventById), pure);
const EventLabelEnhanced = eventLabelEnhancer(Label);
EventLabelEnhanced.propTypes = {
    // eslint-disable-next-line
    eventId: PropTypes.any,
};

const tournamentIdResolver = tournamentId => (
    <span style={wrapperStyle}>
        {space}
        in
        {space}
        <InfoChip>
            <TtLabelEnhanced tournamentId={tournamentId} />
        </InfoChip>
    </span>
);

const timeResolver = (time, isMinute = false) => {
    const appendix = isMinute ? 'minute(s)' : 'time';
    const label = isAny(time) ? 'any ' + appendix : time;

    return (
        <span style={wrapperStyle}>
            {space}at <InfoChip>{label}</InfoChip>
            {isMinute && appendix}
        </span>
    );
};

const dateTimeResolver = time => {
    const label = isAny(time) ? 'any ' + 'time' : time;

    return (
        <span style={wrapperStyle}>
            {space}at <InfoChip>{label}</InfoChip>
        </span>
    );
};

const incidentTypeResolver = incidentType => (
    <span style={wrapperStyle}>
        {space}
        <InfoChip>{humanize(incidentType)}</InfoChip> incident
    </span>
);

const statusResolver = status => (
    <span style={wrapperStyle}>
        {space}when status{space}
        <InfoChip>{humanize(status)}</InfoChip>
    </span>
);

const roundResolver = round => (
    <span style={wrapperStyle}>
        {space}in{space}
        <InfoChip>{humanize(round)}</InfoChip>
        {space}round
    </span>
);

const teamsResolver = (homeId, awayId) => {
    const getTeam = id => (!isAny(id) ? <TeamsLabelEnhanced teamId={id} /> : '*');
    return (
        <span style={wrapperStyle}>
            {space}
            in match{' '}
            <InfoChip labelStyle={yCenter}>
                {getTeam(homeId)} - {getTeam(awayId)}
            </InfoChip>
        </span>
    );
};

const eventIdResolver = eventId => {
    const all = isAny(eventId);
    if (all) {
        return (
            <span style={wrapperStyle}>
                {space}in <InfoChip>any</InfoChip> event
            </span>
        );
    }
    return (
        <span style={wrapperStyle}>
            {space}in event{' '}
            <InfoChip>
                <EventLabelEnhanced eventId={eventId} />
            </InfoChip>
        </span>
    );
};

const jsxArrToInline = arr => (
    // eslint-disable-next-line
    <div style={wrapperStyle}>{arr.map((el, index) => React.cloneElement(el, { key: index }))}</div>
);

/**
 * Function for resolving beauty node
 * depending on triggerType and condition map
 * @param {*} condition
 * @param {*} triggerType
 * @returns {JSX.Element}
 */
const resolveNodeByConditinon = (condition, triggerType) => {
    const result = [];
    const { ttId, eventId, homeId, awayId, incident_type: incidentType, time, dateTime, status, round, minutes } =
        condition || {};
    switch (triggerType) {
        case 'enet_event_incident':
            if (incidentType) {
                result.push(incidentTypeResolver(incidentType));
            }
            if (ttId) {
                result.push(tournamentIdResolver(ttId));
            }
            if (eventId) {
                result.push(eventIdResolver(eventId));
            }
            if (homeId || awayId) {
                result.push(teamsResolver(homeId, awayId));
            }
            return jsxArrToInline(result);
        case 'enet_event_status':
            if (ttId) {
                result.push(tournamentIdResolver(ttId));
            }
            if (eventId) {
                result.push(eventIdResolver(eventId));
            }
            if (homeId || awayId) {
                result.push(teamsResolver(homeId, awayId));
            }
            if (status) {
                result.push(statusResolver(status));
            }
            return jsxArrToInline(result);
        case 'schedule':
            return timeResolver(time);
        case 'time':
            return dateTimeResolver(dateTime);
        case 'tourn_match_day':
            result.push(timeResolver(time));
            result.push(<span>{space}when there are matches</span>);
            if (ttId) {
                result.push(tournamentIdResolver(ttId));
            }
            return jsxArrToInline(result);
        case 'tourn_round_starts':
        case 'tourn_round_ends':
            if (ttId) {
                result.push(tournamentIdResolver(ttId));
            }
            if (time) {
                result.push(timeResolver(time));
            }
            if (round) {
                result.push(roundResolver(round));
            }
            return jsxArrToInline(result);
        case 'event_starts':
        case 'event_halftime':
        case 'event_finished':
        case 'event_test':
            if (ttId) {
                result.push(tournamentIdResolver(ttId));
            }
            if (eventId) {
                result.push(eventIdResolver(eventId));
            }
            if (minutes) {
                result.push(timeResolver(minutes, true));
            }
            return jsxArrToInline(result);
        default:
            return <span />;
    }
};

export class ReadableCondition extends React.Component {
    shouldComponentUpdate({ triggerType, condition }) {
        return this.props.triggerType !== triggerType || !_.isEqual(condition, this.props.condition);
    }

    render() {
        const { triggerType, condition } = this.props;

        if (!triggerType || typeof condition !== 'object') {
            return <div />;
        }

        const node = resolveNodeByConditinon(condition, triggerType);
        return <div>{node}</div>;
    }
}

ReadableCondition.propTypes = {
    condition: PropTypes.object,
    triggerType: PropTypes.string,
};



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/components/ReadableCondition.js
 **/